/**
 * CREWS LIST - Liste compacte d'équipages pour les SidePanels
 */

import { Link } from 'react-router-dom';
import { Users, ExternalLink } from 'lucide-react';
import { Badge } from '../ui';
import { CREW_STATUS_LABELS, CREW_STATUS_COLORS, CREW_PROBABILITY_LABELS } from '@/types/crew';
import type { Crew } from '@/types/crew';
import { ROUTES } from '@/lib/routes/constants';

interface CrewsListProps {
  crews: Crew[];
  loading?: boolean;
}

export function CrewsList({ crews, loading }: CrewsListProps) {
  if (loading) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
        <div className="flex items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-600"></div>
          <span className="ml-2 text-sm text-zinc-500">Chargement...</span>
        </div>
      </div>
    );
  }

  if (crews.length === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-center">
        <p className="text-sm text-zinc-500">Aucun équipage associé</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {crews.map((crew) => (
        <Link
          key={crew.id}
          to={ROUTES.DATA.CREWS.DETAIL(crew.id)}
          className="block p-3 bg-white border border-zinc-200 rounded-md hover:border-zinc-300 hover:shadow-sm transition-all group"
        >
          <div className="flex items-start justify-between gap-3">
            {/* Informations principales */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-medium text-zinc-900 truncate group-hover:text-zinc-700">
                  {crew.name ||
                    (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom')}
                </h4>
                <ExternalLink className="h-3 w-3 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>

              {/* Numéro d'équipage (affiché uniquement si le nom existe) */}
              {crew.crew_number && crew.name && (
                <p className="text-xs text-zinc-500 mb-2">N° {crew.crew_number}</p>
              )}

              {/* Badges : Statut et Probabilité */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  className={`text-xs ${CREW_STATUS_COLORS[crew.status].bg} ${CREW_STATUS_COLORS[crew.status].text} border ${CREW_STATUS_COLORS[crew.status].border}`}
                >
                  {CREW_STATUS_LABELS[crew.status]}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {CREW_PROBABILITY_LABELS[crew.probability]}
                </Badge>
              </div>
            </div>

            {/* Informations secondaires à droite */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {/* Budget */}
              {crew.budget && (
                <span className="text-xs font-medium text-zinc-900">
                  {crew.budget.toLocaleString('fr-FR')} €
                </span>
              )}

              {/* Nombre de membres */}
              {crew.members && crew.members.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                  <Users className="h-3 w-3" />
                  <span>{crew.members.length}</span>
                </div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
